import { api, unwrap, type APIResponse } from "./client";

// Mirrors app/routers/lookup.py DispositionOut.
export interface Disposition {
  disposition_id: number;
  name: string;
  description: string;
}

// Mirrors CodecOut.
export interface Codec {
  codec_id: number;
  codec_name: string;
  codec_value: string;
  active: boolean;
}

export async function listDispositions(): Promise<Disposition[]> {
  const resp = await api.get<APIResponse<Disposition[]>>("/lookup/dispositions");
  return unwrap(resp.data);
}

export async function createDisposition(body: { name: string; description: string }): Promise<number> {
  const resp = await api.post<APIResponse<{ disposition_id: number }>>("/lookup/dispositions", body);
  return unwrap(resp.data).disposition_id;
}

export async function updateDisposition(id: number, body: { name: string; description: string }): Promise<void> {
  await api.put(`/lookup/dispositions/${id}`, body);
}

export async function deleteDisposition(id: number): Promise<void> {
  await api.delete(`/lookup/dispositions/${id}`);
}

export async function listCodecs(): Promise<Codec[]> {
  const resp = await api.get<APIResponse<Codec[]>>("/lookup/codecs");
  return unwrap(resp.data);
}

// --- LNP / number lookup (mirrors lookup.py LnpResult) ---
export interface LnpResult {
  tn: string;
  portable: boolean;
  status: string;
  line_type: string; // "Wireless" | "Landline" | ""
  carrier: string;
  spid: string;
  rate_center: string;
  city: string;
  state: string;
  lata: string;
  min_porting_interval: string;
}

/** Bulk LNP / portability + carrier lookup (gated by the "lnp_lookup" permission). */
export async function lnpLookup(tns: string[]): Promise<LnpResult[]> {
  const resp = await api.post<APIResponse<LnpResult[]>>("/lookup/lnp", { tns });
  return unwrap(resp.data);
}

// --- Deep Verify (EDQ PhoneDemo) — richer, persisted, runs as a background job ---
export interface VerifyResult {
  phone: string;
  phone_number: string | null;
  verify_status: string | null;
  phone_type: string | null;
  ported_date: string | null;
  disposable: string | null;
  operator_name: string | null;
  original_operator: string | null;
  network_status: string | null;
  country: string | null;
  is_ported: string | null;
  success: boolean;
  error: string | null;
}

export interface VerifyJob {
  job_id: number;
  status: "pending" | "processing" | "complete" | "failed";
  total: number;
  done: number;
  verified: number;
  failed: number;
  contact_group_id: number | null;
  pct: number;
  results?: VerifyResult[]; // inline when an ad-hoc job completes
}

/** Deep-verify a pasted batch (<=100). Returns the background job id to poll. */
export async function deepVerify(numbers: string[]): Promise<{ job_id: number; count: number }> {
  const resp = await api.post<APIResponse<{ job_id: number; count: number }>>(
    "/lookup/verify", { numbers });
  return unwrap(resp.data);
}

/** Deep-verify an entire saved contact list. Returns the background job id. */
export async function deepVerifyList(contact_group_id: number): Promise<{ job_id: number; list: string }> {
  const resp = await api.post<APIResponse<{ job_id: number; list: string }>>(
    "/lookup/verify/list", { contact_group_id });
  return unwrap(resp.data);
}

/** Poll a deep-verify job (progress + inline results for ad-hoc jobs). */
export async function getVerifyJob(jobId: number): Promise<VerifyJob> {
  const resp = await api.get<APIResponse<VerifyJob>>(`/lookup/verify/jobs/${jobId}`);
  return unwrap(resp.data);
}

/** Fetch stored deep-verify results for a list or job (paginated; high per_page for export). */
export async function getVerifyResults(
  params: { contact_group_id?: number; job_id?: number; page?: number; per_page?: number },
): Promise<VerifyResult[]> {
  const resp = await api.get<APIResponse<VerifyResult[]>>("/lookup/verify/results", { params });
  return unwrap(resp.data);
}
